/**
 * shadcn Dialog (Tailwind 3 "new-york"), ported per the checklist in
 * agents/CODING_STANDARDS.md -> "Styling: two systems". Radix traps focus in
 * the content and closes on Escape and on an overlay click. On close focus
 * returns to the trigger, or, opened with `open` and no trigger, to whatever
 * had focus when it opened (./return-focus.jsx). Content needs a DialogTitle (Radix warns without one) and a
 * DialogDescription, or `aria-describedby={undefined}` when there is nothing
 * to describe.
 *
 * Overlay and content sit at `tw-z-dialog`: above every legacy modal and
 * drawer (100000), below the toasts and the popovers (tailwind.config.js), so
 * a menu, tooltip or combobox opened inside a dialog is never clipped by it.
 *
 * `hideClose` drops the corner close button for a consumer that renders its
 * own `DialogClose`.
 */
import React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '../../utils/cn.js'
import { Button } from './button.jsx'
import { ReturnFocusRoot, useReturnFocus } from './return-focus.jsx'

const Dialog = props => (
  <ReturnFocusRoot
    root={DialogPrimitive.Root}
    {...props}
  />
)

const DialogTrigger = React.forwardRef((props, ref) => (
  <DialogPrimitive.Trigger
    ref={ref}
    data-slot="dialog-trigger"
    {...props}
  />
))
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName

const DialogPortal = DialogPrimitive.Portal

const DialogClose = React.forwardRef((props, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    data-slot="dialog-close"
    {...props}
  />
))
DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'tw-fixed tw-inset-0 tw-z-dialog tw-bg-black/80 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(
  (
    { className, children, hideClose = false, onCloseAutoFocus, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        onCloseAutoFocus={useReturnFocus(onCloseAutoFocus)}
        className={cn(
          'tw-fixed tw-left-1/2 tw-top-1/2 tw-z-dialog tw-grid tw-w-full tw-max-w-lg -tw-translate-x-1/2 -tw-translate-y-1/2 tw-gap-4 tw-border tw-bg-background tw-p-6 tw-text-foreground tw-shadow-lg tw-duration-200 sm:tw-rounded-menu',
          'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=closed]:tw-slide-out-to-top-[48%] data-[state=open]:tw-slide-in-from-left-1/2 data-[state=open]:tw-slide-in-from-top-[48%]',
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="tw-absolute tw-right-3 tw-top-3 tw-text-muted-foreground hover:tw-text-foreground"
            >
              <X aria-hidden="true" />
              <span
                data-slot="dialog-close-label"
                className="tw-sr-only"
              >
                Close
              </span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div
    data-slot="dialog-header"
    className={cn(
      'tw-flex tw-flex-col tw-space-y-1.5 tw-text-center sm:tw-text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      'tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    data-slot="dialog-title"
    className={cn(
      'tw-text-lg tw-font-semibold tw-leading-none tw-tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot="dialog-description"
    className={cn('tw-text-sm tw-text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
